import React, { useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  SvgIcon,
  TextField,
  Typography,
  Divider,
  makeStyles
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import AddIcon from '@material-ui/icons/Add';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Popper from '@material-ui/core/Popper';
import { DatePickerRangeComponent } from 'src/components';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { convertToLocalISO, dateToday, getDateRangePreset } from 'src/utils';

// ─── Types ───────────────────────────────────────────────────────────────────

type DatePreset = 'today' | 'yesterday' | 'month';
type SortDirection = 'ASC' | 'DESC';

export interface ToolbarState {
  search?: string;
  sortDirection?: SortDirection;
  dateFrom?: string | null;
  dateTo?: string | null;
  datePreset?: DatePreset | 'custom' | null;
}

interface Props {
  onApply?: (state: ToolbarState) => void;
  onAddTicket?: () => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DATE_PRESETS: Array<{ label: string; value: DatePreset }> = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'This Month', value: 'month' }
];

const SORT_OPTIONS: Array<{ label: string; value: SortDirection }> = [
  { label: 'Latest to oldest', value: 'DESC' },
  { label: 'Oldest to latest', value: 'ASC' }
];

const INITIAL_STATE: ToolbarState = {
  search: '',
  sortDirection: 'DESC',
  dateFrom: null,
  dateTo: null,
  datePreset: 'today'
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getPresetDateRange = (preset: DatePreset) => {
  const range = getDateRangePreset(preset);
  if (!range) return { from: null, to: null };
  return {
    from: convertToLocalISO(range.start),
    to: convertToLocalISO(range.end, true)
  };
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: { margin: theme.spacing(2) },
  dateRangeWrapper: {
    '& .MuiFormControl-root': { minWidth: 0, flex: 1 },
    '& .MuiInputBase-root': { height: 40 },
    '& .MuiInputBase-input': { paddingTop: 10, paddingBottom: 10 },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 12px) scale(1)'
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)'
    }
  }
}));

// ─── Component ───────────────────────────────────────────────────────────────

const Toolbar = ({ onApply, onAddTicket }: Props) => {
  const classes = useStyles();

  const [state, setState] = useState<ToolbarState>(INITIAL_STATE);
  const [datePopperAnchor, setDatePopperAnchor] = useState<null | HTMLElement>(
    null
  );

  const updateState = (patch: ToolbarState) =>
    setState((prev) => ({ ...prev, ...patch }));

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handlePresetClick = (preset: DatePreset) => {
    const { from, to } = getPresetDateRange(preset);
    const next = { ...state, datePreset: preset, dateFrom: from, dateTo: to };
    setState(next);
    setDatePopperAnchor(null);
    onApply?.(next);
  };

  const handleCustomDateChange = (
    date: MaterialUiPickersDate,
    field: 'from' | 'to'
  ) => {
    const value = date ? convertToLocalISO(date, field === 'to') : null;
    updateState({
      datePreset: 'custom',
      ...(field === 'from' ? { dateFrom: value } : { dateTo: value })
    });
  };

  const handleSearch = () => {
    onApply?.(state);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleSortChange = (direction: SortDirection) => {
    const next = { ...state, sortDirection: direction };
    setState(next);
    onApply?.(next);
  };

  const isDatePopperOpen = Boolean(datePopperAnchor);
  const activePreset = state.datePreset;

  return (
    <div
      // className={classes.root}
      style={{ padding: 0, marginTop: 15 }}
    >
      <Card>
        <Grid
          container
          spacing={1}
          style={{ padding: '1em', alignItems: 'center' }}
        >
          {/* Search */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
              <TextField
                value={state.search}
                onChange={(e) => updateState({ search: e.target.value })}
                onKeyPress={handleKeyPress}
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Search customer"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SvgIcon fontSize="small" color="action">
                        <SearchIcon />
                      </SvgIcon>
                    </InputAdornment>
                  ),
                  style: { height: 40 }
                }}
              />
              <Button
                onClick={handleSearch}
                color="primary"
                variant="contained"
                style={{ height: 40, minWidth: 40, padding: 0 }}
              >
                <SearchIcon />
              </Button>
            </Box>
          </Grid>

          {/* Sort */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              label="Sort"
              value={state.sortDirection}
              onChange={(e: any) => handleSortChange(e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
              style={{ width: '100%' }}
            >
              {SORT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Date presets + custom picker trigger */}
          <Grid item xs={12} sm={7} md={6} lg={5}>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <ButtonGroup color="primary" size="small" fullWidth>
                {DATE_PRESETS.map((preset) => (
                  <Button
                    key={preset.value}
                    variant={
                      activePreset === preset.value ? 'contained' : 'outlined'
                    }
                    onClick={() => handlePresetClick(preset.value)}
                    style={{ height: 40, fontSize: '0.6em' }}
                  >
                    {preset.label}
                  </Button>
                ))}
                <Button
                  variant={activePreset === 'custom' ? 'contained' : 'outlined'}
                  onClick={(e) =>
                    setDatePopperAnchor(
                      isDatePopperOpen ? null : e.currentTarget
                    )
                  }
                  style={{ height: 40 }}
                >
                  <AccessTimeIcon fontSize="small" />
                </Button>
              </ButtonGroup>
            </Box>
          </Grid>

          <Grid item xs={12} sm={5} md={12} lg={2}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                color="primary"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onAddTicket}
                style={{ height: 40 }}
              >
                Add Ticket
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* Custom date range popper */}
      <Popper
        open={isDatePopperOpen}
        anchorEl={datePopperAnchor}
        placement="bottom-start"
        style={{ zIndex: 10 }}
      >
        <ClickAwayListener onClickAway={() => setDatePopperAnchor(null)}>
          <Paper elevation={10} style={{ padding: 16, minWidth: 320 }}>
            <Typography variant="h6" gutterBottom>
              Custom Date Range
            </Typography>
            <Divider style={{ marginBottom: 12 }} />

            <div className={classes.dateRangeWrapper}>
              <DatePickerRangeComponent
                dateFromLabel="Start Date"
                dateToLabel="End Date"
                fromDateMax={dateToday('yyyy-MM-dd')}
                fromDateValue={state.dateFrom}
                toDateValue={state.dateTo}
                toDateMin={state.dateFrom}
                toDateMax={dateToday('yyyy-MM-dd')}
                onChangeFromDate={(date) =>
                  handleCustomDateChange(date, 'from')
                }
                onChangeToDate={(date) => handleCustomDateChange(date, 'to')}
              />
            </div>

            <Box
              display="flex"
              justifyContent="flex-end"
              mt={2}
              style={{ gap: 8 }}
            >
              <Button
                size="small"
                onClick={() => {
                  const next = {
                    ...state,
                    dateFrom: null,
                    dateTo: null,
                    datePreset: null
                  };
                  setState(next);
                  setDatePopperAnchor(null);
                  onApply?.(next);
                }}
              >
                Clear
              </Button>
              <Button
                size="small"
                color="primary"
                variant="contained"
                onClick={() => {
                  setDatePopperAnchor(null);
                  onApply?.(state);
                }}
              >
                Apply
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};

export default Toolbar;
